'use client'

import RadixTooltip from "react-radix-tooltip";
import { ComponentType, useEffect, useState } from 'react';
import { LoadGameComponentProps, LoadGameProps } from '@/db/schema';
import Link from 'next/link';
import SvgIcons from '@/components/SvgIcons';
import ShareContent from '@/components/ShareContent';
import usePwaStatus from '@/helpers/usePwaStatus';
import DisplayProgressCircles from '@/components/DisplayProgressCircles';
import dynamic from 'next/dynamic';
import './LoadGameComponent.css';
import FlashContentDisplay from './FlashContentDisplay';
import { isAppInstalled } from '@/helpers/helperFunctions';
import { useInstall } from '@/contexts/InstallContext';


const { InstallIcon, WorkIcon } = SvgIcons();

const LoadGameComponent = ( props : LoadGameComponentProps) => {
    const { deferredPrompt, handleInstallClick } = useInstall();
    const { isPWA } = usePwaStatus();
    const [ baseUrl, setBaseUrl ] = useState<string | null>(null);
    const [ shouldShowPrompt, setShouldShowPrompt ] = useState(false);
    const [ ReactGame, setReactGame ] = useState<ComponentType<LoadGameProps> | null>(null);
    const [ loadGameError, setLoadGameError ] = useState(false);
    const { item, prev, next, home = false, profile, token } = props;
    const { SkipPreviousIcon, SkipNextIcon } = SvgIcons();
    const itemUrl = `${baseUrl??process.env.NEXT_PUBLIC_BASE_URL}/game/${item.uri ? item.uri : item.id}`;
    const baseApiUrl = `${baseUrl ? `${baseUrl}/api/` : process.env.NEXT_PUBLIC_API_URL}`;

    useEffect(() => {
        setBaseUrl(window.location.origin);

        // document.querySelectorAll('ruffle-embed')?.forEach((player: any) => { player.volume = item.category === 'Flash' ? 1 : 0; });

        return () => {
            // document.querySelectorAll('ruffle-embed')?.forEach((player: any) => { player.volume = 0; if (player.pause) player.pause(); });
            Array.from(document.getElementsByClassName('relative')).forEach((element) => {
                element.classList.remove('loading');
            });
        }
    }, []);

    useEffect(() => {
        const checkInstallation = async () => {
            const manifestUrl = `${window.location.origin}/game/${item.uri ? item.uri : item.id}/manifest.json`;
            const installed = await isAppInstalled(manifestUrl);
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
            setShouldShowPrompt(!installed && !isStandalone);
        };
        checkInstallation();
    }, [item.uri, item.id]);

    useEffect(() => {
        try {
            if (item.category === 'React') {
                const DynamicComponent = dynamic<LoadGameProps>(() => import(`@/games/${item.url}/${item.url}`), {
                    loading: () => <div style={{ display: 'grid', placeItems: 'center', height: 'calc(100vh - 245px)'}}><DisplayProgressCircles /></div>,
                    ssr: false,
                });
                setReactGame(() => DynamicComponent);
            } else if (item.category === 'Flash') {
                // setReactGame(() => <FlashContentDisplay src={`/assets/swf/game/${item.url}/${item.url}.swf`} />);
            }
        } catch (error) {
            setLoadGameError(true);
            console.log('Game Load Error:', error);
        }
    }, [item.category, item.url]);

    useEffect(() => {
        if (!home) {
            document.getElementsByClassName('navbar')[0]?.classList.add('forcedShrink');
        }

        return () => {
            if (!home) {
                document.getElementsByClassName('navbar')[0]?.classList.remove('forcedShrink');
            }
        };
    }, [home]);

    useEffect(() => {
        if (!item.class) return;

        const elements = document.querySelectorAll('.navbar, .footerNavbar, .footer, .mainContainer');

        if (!home) {
           elements.forEach(el => el.classList.add(item.class!));
            // setTimeout(() => {
            //     const elements = document.querySelectorAll('.navbar, .footerNavbar, .footer, .mainContainer');
            //     elements.forEach(el => el.classList.add(item.class!));
            // }, 500);
        }

        Array.from(document.getElementsByClassName('buttonContainer')).map((el) => {
            if (item.class) el.classList.add(item.class);
        });

        return () => {
            if (!home) {
                elements.forEach(el => el.classList.remove(item.class!));
            }

            Array.from(document.getElementsByClassName('buttonContainer')).map((el) => {
                if (item.class) el.classList.remove(item.class);
            });
        };

    }, [home, item]);

    useEffect(() => {
        const hoomanLink = document.getElementById('thehoomanlink');
        if (!home && item?.images && item?.images[0]) {
            if (hoomanLink) {
                hoomanLink.innerHTML = typeof item?.images[0] === 'string'
                    ? `<img src=${window.location.origin}/media/game/${item?.images[0]} id="thehooman" alt="thehooman" />`
                    : `<img src=${window.location.origin}/media/game/${item?.images[0]?.url} id="thehooman" alt="thehooman" />`;
            }
        }

        return () => {
            if (hoomanLink) {
                hoomanLink.innerHTML = `<img src=${window.location.origin}/assets/image/RepulsiveCoder.webp id="thehooman" alt="thehooman" />`;
            }
        };
    }, [home, item?.images]);

    // const ReactGame = gameComponents[item.url??''];

    if (loadGameError) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', height: 'calc(100vh - 350px)'}}>
                <div className="errorMsg">Game Not Found</div>
            </div>
        );
    }

    // const ReactGame = dynamic<LoadGameProps>(() => import(`@/games/${item.url}/${item.url}`), { ssr: false, });
    // const ReactGame = useMemo(
    //     () =>
    //         dynamic<LoadGameProps>(
    //             () => import(`@/games/${item.url}/${item.url}`),
    //             { ssr: false }
    //         ),
    //     [item.url]
    // );


    return (
        <>
            {ReactGame && <ReactGame profile={profile} token={token} {...props}
                baseApiUrl={baseApiUrl}
                baseShareUrl={itemUrl}
                displayInstallPrompt={shouldShowPrompt && deferredPrompt ? (({ color }) => (
                    <RadixTooltip
                            title={`Install "${item.title}" for better experience`}
                            placement="top"
                    >
                        <button className='absolute left-0 flex items-center justify-center' onClick={() => handleInstallClick()}>
                            <InstallIcon width='24px' height='24px' color={color} />
                        </button>
                    </RadixTooltip>
                )) : null}
            />}

            {item.category === 'Flash' && (
                <FlashContentDisplay title={item.title} src={`/assets/swf/game/${item.url}/${item.url}.swf`} footer={item.instruction} />
            )}

            {home && (
                <div className="buttonContainer mt-1! mb-1! px-0! size-full!" style={{ position: 'relative' }}>
                    <Link className="button" href={`/game/${item.uri}`} prefetch={true}>Insall &quot;{item.title}&quot; on your device</Link>
                    <Link className="button" href="/games" prefetch={true}>Go to Games</Link>
                </div>
            )}

            {!home && !isPWA && (
            <div className={`buttonContainer`} style={{ position: 'relative' }}>
                {prev && prev.images && <link rel="preload" as="image" href={`/media/game/${typeof prev.images[0] === 'string' ? prev.images[0] : prev.images[0].url}`} />}
                {next && next.images && <link rel="preload" as="image" href={`/media/game/${typeof next.images[0] === 'string' ? next.images[0] : next.images[0].url}`} />}
                <span className="prev">
                    {prev && (
                        <RadixTooltip
                            title={
                                <div className="tooltipContent">
                                    <h2>{prev.title.replace(/[\u00A0-\u9999<>&]/g, i => '&#'+i.charCodeAt(0)+';')}</h2>
                                    {prev.images && <img src={`/media/game/${typeof prev.images[0] === 'string' ? prev.images[0] : prev.images[0].url}`} alt='gameImage' />}
                                </div>
                            }
                            placement="top"
                        >
                            <Link className='relative' href={`/game/${prev.uri !== undefined && prev.uri !== null ? prev.uri : prev.id}`} prefetch={true} onClick={(e) => (e.target as HTMLElement)?.classList.add('loading')}>
                                <SkipPreviousIcon width='24px' height='24px' />
                                <span className='loading absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-60'>
                                    <DisplayProgressCircles n={1} />
                                </span>
                            </Link>
                        </RadixTooltip>
                    )}
                </span>
                <Link className="button" href="/games" prefetch={true}>Back to Games</Link>
                <span className="next">
                    {next && (
                        <RadixTooltip
                            title={
                                <div className="tooltipContent">
                                    <h2>{next.title.replace(/[\u00A0-\u9999<>&]/g, i => '&#'+i.charCodeAt(0)+';')}</h2>
                                    {next.images && <img src={`/media/game/${typeof next.images[0] === 'string' ? next.images[0] : next.images[0].url}`} alt='gameImage' />}
                                </div>
                            }
                            placement="top"
                        >
                            <Link className='relative' href={`/game/${next.uri !== undefined && next.uri !== null ? next.uri : next.id}`} prefetch={true} onClick={(e) => (e.target as HTMLElement)?.classList.add('loading')}>
                                <SkipNextIcon width='24px' height='24px' />
                                <span className='loading absolute top-0 left-0 w-full h-full flex items-center justify-center opacity-60'>
                                    <DisplayProgressCircles n={1} />
                                </span>
                            </Link>
                        </RadixTooltip>
                    )}
                </span>
                <ShareContent url={itemUrl} tooltip='Share this Game' title={'Share this Game with Friends'} style={{ position: 'absolute', top: 16, right: -10 }} />
            </div>
            )}
            {!home && !isPWA && (
                <div className={`buttonContainer fixed right-5 bottom-6 transition-all duration-1000 z-200 m-0! w-auto! p-0!`}>
                    <Link
                        href={`/work-with-me?url=${encodeURIComponent(`${baseUrl}/game/${item.uri ? item.uri : item.id}`)}`}
                        className={`button mx-auto! gap-6! work-with-me-small no-text-shadow`}
                        prefetch={true}
                    >
                        <WorkIcon width='32px' height='32px' style={{ margin: '-10px' }} />
                        Like what you see? → Work with Me
                    </Link>
                </div>
            )}
        </>
    );
}

export default LoadGameComponent;